<template>
  <tr class="b2b-grid__product-row">
    <td v-if="!forPrint" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.icon">
      <grid-icon :data="{value: 'card_giftcard'}"></grid-icon>
    </td>

    <td v-if="b2bProductPriority" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.sold">
      <toggle-switch
        v-if="item.isBTO"
        :id="`${item.sku}-sold-toggle`"
        :name="`${item.sku}-sold-toggle`"
        :value="alreadySoldToConsumer"
        :sync="true"
        qaid="sku-grid.sold.toggle"
        @change="onSoldToggle"
      />
    </td>

    <td v-if="!forPrint" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.note">
      <grid-note
        v-if="productHasNote || (b2bProductPriority && item.isBTO)"
        ref="gridNote"
        :data="{
          value: 'note_add',
          editable: b2bProductPriority && item.isBTO,
          content: item.lineNote,
          hasNotification: productHasNote,
        }"
        :item-id="item.sku"
        :max-length="60"
        :base-properties="baseProperties"
        :placeholder-text="bp('noteRequired')"
        @note-added="handleAddNote"
        @tooltip-closed="handleTooltipClosed"
      ></grid-note>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.split">
      <grid-icon v-if="item.isSplitWarehouse" :data="{value: 'call_split'}"></grid-icon>
    </td>

    <td v-if="isProjectOne" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.sku">
      <div class="b2b-grid__label">{{ getHeader('sku') }}</div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.sku}" />
      </div>
    </td>

    <td v-else class="b2b-grid__cell lg:table-cell" qaid="sku-grid.sku">
      <div class="b2b-grid__label">{{ getHeader('sku') }}</div>
      <div class="b2b-grid__content">
        <div class="flex items-center">
          <a
            :href="`${encodedContextPath}/my-account/orders?partNumber=${item.sku}`"
            class="leading-none mr-1"
            qaid="sku-grid.orderHistory"
          >
            <i class="material-icons">history</i>
          </a>
          <grid-text
            class="flex"
            :data="{
              value: item.sku,
              tooltip: getTooltipText(item, false),
              link: encodedContextPath + '/p/' + item.sku,
            }"
          ></grid-text>
        </div>
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.item">
      <div class="b2b-grid__label">{{ getHeader('item') }}</div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.itemName}"></grid-text>
      </div>
    </td>

    <td v-if="!isProjectOne" class="b2b-grid__cell lg:table-cell" qaid="sku-grid.upc">
      <div class="b2b-grid__label">{{ getHeader('upc') }}</div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.ean ? item.ean : item.upc}"></grid-text>
      </div>
    </td>

    <td v-if="isCustomWaterBottle" class="b2b-grid__cell lg:table-cell h-auto lg:text-right" qaid="sku-grid-custom-id">
      <div class="b2b-grid__label" v-html="bp('customId')" />
      <div class="b2b-grid__content b2b-grid__content--right">
        <trek-link qaid="cart-grid-custom-id-link" underline text @click="redirectToSpectrumCustomizer()">
          <grid-text :data="{value: item.productRecipeID}" />
        </trek-link>
      </div>
    </td>

    <td v-else-if="isProjectOne" class="b2b-grid__cell lg:table-cell h-auto lg:text-right" qaid="sku-grid-custom-id">
      <div class="b2b-grid__label" v-html="bp('customId')" />
      <div class="b2b-grid__content b2b-grid__content--right">
        <trek-link qaid="cart-grid-custom-id-link" underline text @click="redirectToProjectOneSummaryPage()">
          <grid-text :data="{value: item.p1ConfigurationId}" />
        </trek-link>
      </div>
    </td>

    <template v-else>
      <td
        v-if="checkoutStep != 'confirmation'"
        class="b2b-grid__cell lg:table-cell text-left lg:text-right"
        qaid="sku-grid.allocated"
      >
        <div class="b2b-grid__label">{{ getHeader('allocated') }}</div>
        <div class="b2b-grid__content">
          <grid-text :data="{value: item.quantityAllocatedDI == 0 ? null : item.quantityAllocatedDI}"></grid-text>
        </div>
      </td>

      <td
        v-if="checkoutStep != 'confirmation' && !isProjectOne"
        class="b2b-grid__cell lg:table-cell text-left lg:text-right"
        qaid="sku-grid.backordered"
      >
        <div class="b2b-grid__label">{{ getHeader('backordered') }}</div>
        <div class="b2b-grid__content  b2b-grid__content--right">
          <trek-link
            v-if="item.quantityBackorderedDI"
            qaid="checkout-grid-backordered-link"
            underline
            button
            xsmall
            text
            @click="redirectToBackorderAllocated()"
          >
            <grid-text :data="{value: item.quantityBackorderedDI}" />
          </trek-link>
        </div>
      </td>
    </template>

    <td
      v-if="!consumerFriendlyMode && !isProjectOne"
      class="b2b-grid__cell lg:table-cell text-left lg:text-right"
      qaid="sku-grid.order-price"
    >
      <div class="b2b-grid__label">{{ getHeader('price') }}</div>
      <div class="b2b-grid__content">
        <grid-price :data="{dealerCost: item.price.dealerCost, discount: discount}"></grid-price>
      </div>
    </td>

    <td
      v-if="checkoutStep != 'confirmation' && !isProjectOne"
      qaid="sku-grid.list"
      class="b2b-grid__cell lg:table-cell lg:text-right"
    >
      <div class="b2b-grid__label"><i class="md-18">list</i></div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.skuQtyInSavedList > 0 ? item.skuQtyInSavedList : ''}"></grid-text>
      </div>
    </td>

    <td class="b2b-grid__cell lg:table-cell" qaid="sku-grid.qty">
      <div class="b2b-grid__label">{{ getHeader('quantity') }}</div>
      <div class="b2b-grid__content">
        <grid-text v-if="!isBackorder" key="grid-qty" :data="{value: item.nonBackorderedQty}"></grid-text>
        <grid-text v-else key="grid-qty" :data="{value: item.backorderedQty}"></grid-text>
      </div>
    </td>

    <td v-if="!consumerFriendlyMode" class="b2b-grid__cell lg:table-cell text-right" qaid="sku-grid.subtotal">
      <div class="b2b-grid__label">{{ getHeader('subtotal') }}</div>
      <div class="b2b-grid__content">
        <grid-text :data="{value: item.formattedLineTotal}"></grid-text>
      </div>
    </td>
  </tr>
</template>

<script>
import axios from 'axios';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import {mapState, mapGetters} from 'vuex';
import GridIcon from '@/components/grid/GridIcon';
import GridNote from '@/components/grid/GridNote';
import GridText from '@/components/grid/GridText';
import GridPrice from '@/components/grid/GridPrice';
import TrekLink from '@/components/TrekLink';
import ToggleSwitch from '@/components/ToggleSwitch';

export default {
  components: {GridIcon, GridNote, GridText, GridPrice, TrekLink, ToggleSwitch},

  props: {
    item: {
      type: Object,
      default: () => {},
    },
    headers: {
      type: Array,
      default: () => [],
    },
    isBackorder: Boolean,
    forPrint: {
      type: Boolean,
      default: false,
    },
    checkoutStep: {
      type: String,
      default: '',
    },
    baseProperties: {
      type: Array,
      default: () => [],
      required: false,
    },
    partTypeKey: {
      type: String,
      default: '',
    },
    b2bProductPriority: {
      type: Boolean,
      default: false,
      required: false,
    },
    cartId: {
      type: String,
      default: '',
      required: false,
    },
    isCustomWaterBottle: {
      type: Boolean,
      default: false,
      required: false,
    },
    isProjectOne: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  data() {
    return {
      alreadySoldToConsumer: false,
    };
  },

  computed: {
    productHasNote() {
      return this.item && !isEmpty(this.item.lineNote);
    },

    discount() {
      if (this.item && this.item.price) {
        if (this.item.price.orderedPrice) {
          return this.item.price.orderedPrice;
        } else if (this.advancedOrderingMode && this.item.price.advancedPrice) {
          return this.item.price.advancedPrice;
        } else if (this.item.price.sale && Number(this.item.price.sale.replace(/[^0-9.-]+/g, '')) > 0) {
          return this.item.price.sale;
        }
      }

      return 0;
    },

    ...mapState('advancedOrdering', ['advancedOrderingMode']),
    ...mapState('backend', ['encodedContextPath']),
    ...mapGetters('backend', ['consumerFriendlyMode']),
  },

  mounted() {
    this.alreadySoldToConsumer = this.item.alreadySoldToConsumer;
  },

  methods: {
    getHeader(headerName) {
      let header = find(this.headers, (header) => header.name === headerName);
      return header ? header.label : '';
    },

    handleAddNote({id, content}) {
      if (content == null || content.length < 1) {
        this.alreadySoldToConsumer = false;
      }

      const payload = {
        sku: id,
        lineNote: content,
        alreadySoldToConsumer: this.alreadySoldToConsumer,
      };

      this.updateCart([payload]);
    },

    handleTooltipClosed({dirtyContent}) {
      if (dirtyContent === null || dirtyContent.length < 1) {
        this.alreadySoldToConsumer = false;
      }
    },

    getTooltipText(item, onlyMFR) {
      const replacementProduct = item.replacementProduct ?? null;
      const mfr = item.mfr ?? null;
      const replacementProductText = this.getBaseProperty('archiveViewCurrent');
      let toolTipData = {};
      if (mfr !== null) {
        toolTipData['MFR'] = mfr;
      }
      if (replacementProduct && !onlyMFR) {
        toolTipData['replacementProductText'] = replacementProductText;
        toolTipData['replacementProductUrl'] = replacementProduct.url;
      }
      return toolTipData;
    },

    redirectToBackorderAllocated() {
      const productType = this.partTypeKey === 'bikes' ? 'bike' : 'am';
      const redirectPath = `${this.encodedContextPath}/my-account/backorders-allocated-items?productType=${productType}&skuSearchTerm=${this.item.sku}`;
      window.location.href = redirectPath;
    },

    redirectToSpectrumCustomizer() {
      if (this.item.sku && this.item.productRecipeID) {
        const redirectPath = `${this.encodedContextPath}/p/${this.item.sku}?hasSpectrumOpen=true&recipeId=${this.item.productRecipeID}`;
        window.location.href = redirectPath;
      }
    },

    redirectToProjectOneSummaryPage() {
      if (this.item.p1ConfigurationId) {
        const redirectPath = `${this.encodedContextPath}/project-one-builder/#/${this.item.p1ConfigurationId}/summary`;
        window.location.href = redirectPath;
      }
    },

    onSoldToggle(event) {
      // If the value of the toggle is different from what we already have
      if (event && event.value != this.alreadySoldToConsumer) {
        // Then assign the new value to our local data
        this.alreadySoldToConsumer = event.value;

        if (this.alreadySoldToConsumer) {
          // If the toggle has been turned on then open the note
          this.$refs.gridNote.openTooltip();
        } else {
          // If the toggle has been turned off then update the cart
          const payload = {
            sku: this.item.sku,
            lineNote: this.item.lineNote,
            alreadySoldToConsumer: this.alreadySoldToConsumer,
          };

          this.updateCart([payload]);
        }
      }
    },

    updateCart(payload) {
      axios.put(`/cart/carts/${this.cartId}/entries`, {cartEntries: payload}).then(() => {
        // This event causes the entire page to reload its data
        this.$emit('update-item-bto-data', payload[0]);
      });
    },
  },
};
</script>
